/* cSpell:ignore noneditable */
import React from "react";

import { faMemoPad, faTrashCan } from "@performyard/fontawesome/pro-regular-svg-icons";
import { ButtonIcon, Icon, Text, cn } from "@performyard/py-components";

export type ReviewFormContainerProps = {
	/**
	 * The name of the form to display in the header
	 */
	name: string;
	/**
	 * Whether this form is from a template (shows template icon)
	 */
	fromTemplate?: boolean;
	/**
	 * Optional callback when delete button is clicked
	 * If provided, a delete button will be shown in the header
	 */
	onDelete?: () => void;
	/**
	 * The content of the form card (form fields)
	 */
	children: React.ReactNode;
	/**
	 * Optional CSS classes to apply to the container
	 */
	dangerouslySetClassName?: string;
	/**
	 * Test identifier for the container
	 */
	"data-testid"?: string;
};

/**
 * ReviewFormContainer is the base container component for all review form selection cards.
 *
 * This component should be used as the wrapper for all form type variants (Standard, 360, External).
 */
export const ReviewFormContainer = ({
	name,
	fromTemplate,
	onDelete,
	children,
	dangerouslySetClassName,
	"data-testid": dataTestId,
}: ReviewFormContainerProps) => {
	return (
		<div
			className={cn(
				"flex w-full flex-col overflow-hidden rounded-1 border border-solid border-neutral-3 bg-neutral-0 shadow-xs",
				dangerouslySetClassName
			)}
			data-testid={dataTestId || "review-form-container"}
		>
			<div className="flex h-10 items-center justify-between gap-2 bg-neutral-1 px-4">
				<div className="flex min-w-0 flex-1 items-center gap-2">
					{fromTemplate && (
						<Icon
							icon={faMemoPad}
							size={20}
							color="neutral-7"
							aria-label="From template"
							data-testid="template-icon"
						/>
					)}
					<Text
						size="paragraph-3"
						weight="semi-bold"
						color="neutral-9"
						overrideElement="span"
						dangerouslySetClassName="flex-1 py-line-clamp-1"
						data-testid="form-name"
					>
						{name}
					</Text>
				</div>
				{onDelete && (
					<ButtonIcon
						icon={faTrashCan}
						variant="outline"
						color="neutral"
						size={24}
						onClick={onDelete}
						aria-label="Delete form"
						data-testid="delete-button"
					/>
				)}
			</div>

			<div className="flex flex-col gap-2 bg-neutral-0 p-4">{children}</div>
		</div>
	);
};

ReviewFormContainer.displayName = "ReviewFormContainer";
